/**
 * Per-user pin list for WorkList ordering. Mirrors the WhatsApp
 * pinned-chats pattern: pinned work ids float to the top of the list,
 * the rest follow in natural order.
 *
 * Persisted to localStorage so a logout/re-pair flow that wipes
 * IndexedDB still keeps the user's pins. Cross-device sync (mobile↔web,
 * web↔web of the same admin) flows through the team `sync` channel
 * via a `pins_sync` message — see `useLiveSync.ts`. The `applyRemotePins`
 * entry point is what the sync handler calls when a pins_sync arrives
 * for the local userId; it overwrites the cache + localStorage and
 * fires only the UI listener pool, NOT the local-change pool, so the
 * outgoing-broadcast effect doesn't bounce the same set back to peers.
 *
 * Two listener pools:
 *   _uiListeners         — fired on every cache change (local OR remote).
 *                          UI subscribers (WorkList) attach here.
 *   _localChangeListeners — fired only on toggle/unpin (local mutations).
 *                          The outgoing-sync effect attaches here, so a
 *                          remote `pins_sync` that updates the cache
 *                          doesn't trigger a re-broadcast loop.
 */

const STORAGE_KEY = 'cuadrilla:pinned_works';

let _cache: Set<string> | null = null;
const _uiListeners = new Set<() => void>();
const _localChangeListeners = new Set<() => void>();

function _hydrate(): Set<string> {
  if (_cache) return _cache;
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    _cache = new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    _cache = new Set();
  }
  return _cache;
}

function _persist(): void {
  if (!_cache) return;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([..._cache]));
    }
  } catch {
    // Storage quota exceeded or disabled — silently drop. The in-memory
    // cache still keeps the toggle alive for this session.
  }
}

function _emitUi(): void {
  _uiListeners.forEach(l => { try { l(); } catch { /* swallow */ } });
}

function _emitLocalChange(): void {
  _localChangeListeners.forEach(l => { try { l(); } catch { /* swallow */ } });
}

/** Snapshot of currently pinned work ids. */
export function getPinnedWorks(): ReadonlySet<string> {
  return _hydrate();
}

/** Toggle pin state for a work id. Returns the new pinned status.
 *  Allocates a fresh Set so `useSyncExternalStore` consumers see a new
 *  reference and re-render — mutating the same Set in place would let
 *  React's identity check (`Object.is`) skip the update. */
export function togglePinnedWork(workId: string): boolean {
  const next = new Set(_hydrate());
  if (next.has(workId)) next.delete(workId);
  else next.add(workId);
  _cache = next;
  _persist();
  _emitUi();
  _emitLocalChange();
  return next.has(workId);
}

/** Drop a pin if a work was deleted/archived elsewhere. No-op if absent.
 *  Same fresh-Set discipline as `togglePinnedWork` so subscribers re-render. */
export function unpinWork(workId: string): void {
  const cur = _hydrate();
  if (!cur.has(workId)) return;
  const next = new Set(cur);
  next.delete(workId);
  _cache = next;
  _persist();
  _emitUi();
  _emitLocalChange();
}

/**
 * Replace the entire pin set from a remote `pins_sync` message. Bypasses
 * the local-change listener pool so the outgoing-broadcast effect does
 * NOT re-send the same set we just received (which would loop forever
 * across two devices of the same user).
 */
export function applyRemotePins(ids: string[]): void {
  // Fully reset rather than diff — LWW by relay delivery order means
  // the freshest broadcast is authoritative, and computing a diff just
  // to fire the same listener once is wasted work.
  _cache = new Set(ids.filter((x): x is string => typeof x === 'string'));
  _persist();
  _emitUi();
}

/** Subscribe to ALL pin changes (local + remote). UI uses this. */
export function subscribePinnedWorks(listener: () => void): () => void {
  _uiListeners.add(listener);
  return () => { _uiListeners.delete(listener); };
}

/**
 * Subscribe to LOCAL pin changes only (toggle/unpin). The outgoing
 * sync effect uses this to broadcast — listening to all changes would
 * cause a remote-applied update to bounce back as a fresh broadcast.
 */
export function subscribeLocalPinChanges(listener: () => void): () => void {
  _localChangeListeners.add(listener);
  return () => { _localChangeListeners.delete(listener); };
}

/** Test seam — drops the cache without touching localStorage. */
export function _resetPinnedWorksForTests(): void {
  _cache = null;
  _uiListeners.clear();
  _localChangeListeners.clear();
}
